import {loadStripe, StripeEmbeddedCheckout} from '@stripe/stripe-js';
import Button from 'components/Button';
import Modal from 'components/Modal';
import {MoneyRequest} from 'pages/Portal/ClientInboxItem/types';
import {FC, useCallback, useContext, useEffect, useRef, useState} from 'react';
import Sentry from 'sentry';
import {toCurrency} from 'utils/number';
import {TEST_STRIPE_KEY} from 'utils/stripe/constants';

import PaymentPanelContext from '../context';
import {
    useGetMoneyRequestIsPaidView,
    usePostCreateCheckoutSessionView,
} from '../services';
import PayButton from './Base';

interface StripeButtonProps {
    moneyRequest: MoneyRequest;
}

const StripeButton: FC<StripeButtonProps> = ({moneyRequest}) => {
    const {
        clearError,
        isLoading,
        pollCount,
        setGenericError,
        setIsLoading,
        setIsPaid,
    } = useContext(PaymentPanelContext);

    const {stripeConfig} = moneyRequest;
    const stripeKey = stripeConfig.stripeKey;
    const stripeVersion = stripeConfig.apiVersion || '2023-08-16';

    // API Endpoints
    const getMoneyRequestIsPaidView = useGetMoneyRequestIsPaidView();
    const postCreateCheckoutSessionView = usePostCreateCheckoutSessionView();

    // Stripe Embedded Checkout Session
    const stripeEmbeddedCheckout = useRef<null | StripeEmbeddedCheckout>(null);
    const stripeEmbeddedCheckoutDiv = useRef<HTMLDivElement>(null);
    const [isModalOpen, setModalOpen] = useState<boolean>(false);

    // Constants
    const isTest = stripeKey === TEST_STRIPE_KEY;

    const onClick = useCallback(async () => {
        setIsLoading(true);
        clearError();

        if (stripeEmbeddedCheckout.current) {
            return setTimeout(() => setModalOpen(true), 500);
        }

        // Step 1: Load Stripe
        const stripe = await loadStripe(stripeKey, {
            apiVersion: stripeVersion,
        });

        if (!stripe) {
            Sentry.captureMessage('Failed to load Stripe library', {
                extra: {
                    charge_mid: undefined,
                    mr_token: moneyRequest.mrToken,
                },
            });

            setGenericError();
            setIsLoading(false);

            return;
        }

        // Step 2: Create a Checkout Session
        const response = await postCreateCheckoutSessionView
            .mutateAsync({
                mrToken: moneyRequest.mrToken,
                palLink: window.location.href.split('?')[0],
            })
            .catch((error) => {
                if (error.message === 'already_paid') {
                    setIsPaid(true);
                } else {
                    setGenericError();
                    setIsLoading(false);
                }
            });

        if (!response) {
            setIsLoading(false);
            return setGenericError();
        }

        // Step 3: Mount the embedded Checkout Session
        if (!isTest) {
            stripeEmbeddedCheckout.current = await stripe.initEmbeddedCheckout({
                clientSecret: response.data.clientSecret,
            });
        }

        // Step 4: Open the Modal
        setModalOpen(true);
    }, [
        setGenericError,
        moneyRequest.mrToken,
        stripeKey,
        postCreateCheckoutSessionView,
    ]);

    const closeModal = useCallback(() => {
        setIsLoading(false);
        clearError();
        setModalOpen(false);
        stripeEmbeddedCheckout.current?.unmount();
    }, []);

    useEffect(() => {
        if (isModalOpen && stripeEmbeddedCheckoutDiv.current) {
            stripeEmbeddedCheckout.current?.mount(
                stripeEmbeddedCheckoutDiv.current,
            );
        }
    }, [isModalOpen]);

    useEffect(() => {
        if (moneyRequest.isPendingPayment && pollCount.current === 0) {
            setIsLoading(true);
            const pollFunction = async () => {
                const response = await getMoneyRequestIsPaidView.mutateAsync({
                    mrMid: moneyRequest.mrMid,
                });

                if (response?.data.paid) {
                    setIsPaid(true);
                } else if (pollCount.current > 30) {
                    setGenericError();
                } else {
                    pollCount.current += 1;
                    setTimeout(pollFunction, 500);
                }
            };
            pollCount.current += 1;
            setTimeout(pollFunction, 500);
        }
    }, [
        setGenericError,
        getMoneyRequestIsPaidView,
        moneyRequest.isPendingPayment,
        moneyRequest.mrMid,
    ]);

    return (
        <>
            <PayButton isLoading={isLoading} onClick={onClick} />
            <Modal
                isOpen={isModalOpen}
                onClose={closeModal}
                preventOutsideClick={true}
            >
                <Modal.Body className="relative h-full p-8">
                    <Button
                        className="absolute right-6 top-6"
                        icon="close"
                        kind="tertiary"
                        onClick={closeModal}
                        size="sm"
                    />
                    <div
                        id="stripe-checkout-div"
                        ref={stripeEmbeddedCheckoutDiv}
                    >
                        {isTest && (
                            <div
                                className="pointer-events-none flex h-[700px] select-none flex-col rounded bg-grey-50 p-8"
                                id="stripe-checkout-dummy"
                            >
                                <div className="text-sm text-light-disabled">
                                    INV-0389 Invoice
                                </div>
                                <div className="text-3xl font-bold text-light-disabled">
                                    {toCurrency({
                                        currency: moneyRequest.currency,
                                        value: moneyRequest.totalCost,
                                    })}
                                </div>
                                <div className="min-h-0 flex-1 pt-2 text-light-disabled">
                                    This is a dummy Stripe Checkout Session.
                                </div>
                                <Button disabled={true}>Pay</Button>
                                <div className="pb-2 pt-8 text-center text-xs text-light-disabled">
                                    Powered by <b>Stripe</b>
                                </div>
                                <div className="flex flex-row justify-center gap-x-4 text-center text-xs text-light-disabled">
                                    <div>Terms</div>
                                    <div>Privacy</div>
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default StripeButton;
